import { types, destroy } from 'mobx-state-tree';

/**
 * Модель одного уведомления
 */
const Notification = types
    .model('Notification', {
        id: types.identifierNumber,
        type: types.enumeration(['success', 'error', 'warning', 'info']),
        message: types.string,
        title: types.maybe(types.string),
        timestamp: types.number,
        autoHide: types.optional(types.boolean, true),
        duration: types.maybe(types.number), // Время показа в мс
    })
    .actions(self => ({
        // Действия на уровне одного уведомления
        dismiss() {
            const parent = self.$parent;
            if (parent && parent.remove) {
                parent.remove(self.id);
            }
        }
    }));

/**
 * MST модель для управления уведомлениями
 */
const NotificationStore = types
    .model('NotificationStore', {
        notifications: types.array(Notification),
        autoHideTimeout: types.optional(types.number, 5000), // 5 секунд по умолчанию
        maxNotifications: types.optional(types.number, 5), // Максимум уведомлений на экране
    })
    .views(self => ({
        // Вычисляемые значения
        get hasNotifications() {
            return self.notifications.length > 0;
        },
        
        get errorCount() {
            return self.notifications.filter(n => n.type === 'error').length;
        },
        
        get warningCount() {
            return self.notifications.filter(n => n.type === 'warning').length;
        },
        
        get latestNotification() {
            return self.notifications[self.notifications.length - 1];
        },
        
        getByType(type) {
            return self.notifications.filter(n => n.type === type);
        }
    }))
    .actions(self => {
        let timers = new Map(); // Таймеры для автоскрытия
        
        // Приватные методы
        const scheduleAutoHide = (notification) => {
            if (notification.autoHide) {
                const timer = setTimeout(() => {
                    self.remove(notification.id);
                }, notification.duration || self.autoHideTimeout);
                
                timers.set(notification.id, timer);
            }
        };
        
        const cancelAutoHide = (id) => {
            if (timers.has(id)) {
                clearTimeout(timers.get(id));
                timers.delete(id);
            }
        };
        
        const checkLimit = () => {
            // Удаляем старые уведомления если превышен лимит
            while (self.notifications.length >= self.maxNotifications) {
                const oldest = self.notifications[0];
                self.remove(oldest.id);
            }
        };
        
        return {
            // Базовый метод добавления
            add(config) {
                checkLimit();
                
                const notification = {
                    id: Date.now() + Math.random(),
                    timestamp: Date.now(),
                    autoHide: config.type !== 'error', // Ошибки не скрываем автоматически
                    ...config
                };
                
                self.notifications.push(notification);
                scheduleAutoHide(notification);
                
                return notification.id;
            },
            
            // Специализированные методы для разных типов
            success(message, options = {}) {
                return self.add({
                    type: 'success',
                    message,
                    ...options
                });
            },
            
            error(message, options = {}) {
                return self.add({
                    type: 'error',
                    message,
                    autoHide: false, // Ошибки требуют внимания
                    ...options
                });
            },
            
            warning(message, options = {}) {
                return self.add({
                    type: 'warning',
                    message,
                    ...options
                });
            },
            
            info(message, options = {}) {
                return self.add({
                    type: 'info',
                    message,
                    ...options
                });
            },
            
            // Удаление уведомления
            remove(id) {
                cancelAutoHide(id);
                const notification = self.notifications.find(n => n.id === id);
                if (notification) {
                    destroy(notification);
                }
            },
            
            // Очистка всех уведомлений
            clear() {
                // Отменяем все таймеры
                timers.forEach(timer => clearTimeout(timer));
                timers.clear();
                
                // Очищаем массив
                self.notifications.clear();
            },
            
            // Очистка по типу
            clearByType(type) {
                const toRemove = self.notifications.filter(n => n.type === type);
                toRemove.forEach(n => self.remove(n.id));
            },
            
            // Настройки
            setAutoHideTimeout(timeout) {
                self.autoHideTimeout = timeout;
            },
            
            setMaxNotifications(max) {
                self.maxNotifications = max;
                checkLimit();
            },
            
            // Управление автоскрытием для конкретного уведомления
            pauseAutoHide(id) {
                cancelAutoHide(id);
            },
            
            resumeAutoHide(id) {
                const notification = self.notifications.find(n => n.id === id);
                if (notification && notification.autoHide) {
                    scheduleAutoHide(notification);
                }
            },
            
            // Lifecycle
            beforeDestroy() {
                // Очищаем все таймеры при уничтожении стора
                timers.forEach(timer => clearTimeout(timer));
                timers.clear();
            },
            
            reset() {
                self.clear();
                self.autoHideTimeout = 5000;
                self.maxNotifications = 5;
            }
        };
    });

export { NotificationStore, Notification };